import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';

/**
 * Generic PopupMenu component
 * Props:
 * - x, y: position (number)
 * - onClose: function
 * - title: string (optional)
 * - items: array of { label, labelNode, onClick, color, icon, disabled, rightAction } (icon/rightAction optional)
 *   Use labelNode for rich labels (React nodes). Raw HTML strings are not rendered.
 * - minWidth: number/string (optional)
 * - style: object (optional)
 */
export default function PopupMenu({ x, y, onClose, title, items = [], minWidth = 160, style = {} }) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ x, y });
  const menuRef = useRef(null);

  useEffect(() => {
    setShow(true);
  }, []);

  // Calculate viewport-constrained position
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let adjustedX = x;
      let adjustedY = y;
      
      // Adjust X position if menu would go off right edge
      if (x + rect.width / 2 > viewportWidth - 10) {
        adjustedX = viewportWidth - rect.width / 2 - 10;
      }
      // Adjust X position if menu would go off left edge
      if (x - rect.width / 2 < 10) {
        adjustedX = rect.width / 2 + 10;
      }
      
      // Adjust Y position if menu would go off bottom edge
      if (y + rect.height > viewportHeight - 10) {
        adjustedY = y - rect.height - 20; // Show above the click point
      }
      // Adjust Y position if menu would go off top edge
      if (adjustedY < 10) {
        adjustedY = 10;
      }
      
      setPosition({ x: adjustedX, y: adjustedY });
    }
  }, [x, y, show]);

  useEffect(() => {
    function handleClickOutside(e) {
      // Check if the clicked element has a data attribute indicating it should open a popup
      const clickedElement = e.target.closest('[data-popup-trigger]');
      if (clickedElement) {
        // Don't close the popup if clicking on an element that should open a popup
        return;
      }
      
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShow(false);
        setTimeout(onClose, 150);
      }
    }

    function handleScroll() {
      // Close popup on scroll (especially useful for mobile)
      setShow(false);
      setTimeout(onClose, 150);
    }
    
    // Add a longer delay to prevent immediate closure when switching between items
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
      document.addEventListener('touchmove', handleScroll, { passive: true }); // Handle mobile scroll
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('touchmove', handleScroll);
    };
  }, [onClose]);

  return createPortal(
    <CSSTransition 
      in={show} 
      timeout={150} 
      classNames="popup-fade" 
      unmountOnExit
      nodeRef={menuRef}
    >
      <div
        ref={menuRef}
        className="popup-menu"
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y - 10,
          transform: 'translateX(-50%)',
          zIndex: 2000,
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0',
          padding: '8px',
          minWidth,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          ...style
        }}
      >
        {title && (
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#64748b',
              padding: '4px 8px',
              borderBottom: '1px solid #e2e8f0',
              marginBottom: '4px',
              position: 'relative',
              maxWidth: '220px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: 'ltr'
            }}
          >
            <span
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                verticalAlign: 'bottom',
                position: 'relative',
                paddingRight: '24px',
                background: 'linear-gradient(to right, #64748b 80%, rgba(100,116,139,0.1) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent'
              }}
            >
              {title.length > 35 ? title.slice(0, 35) + '...' : title}
            </span>
          </div>
        )}
        {items.map((item, idx) => (
          <div
            key={idx}
            className="popup-menu-item-row"
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: '4px',
              width: '100%'
            }}
          >
            <button
              className={`popup-menu-item ${item.className || ''}`.trim()}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s ease',
                color: item.color || '#374151',
                opacity: item.disabled ? 0.5 : 1
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = item.color === '#dc2626' ? '#fef2f2' : '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              disabled={item.disabled}
            >
              {item.icon && (
                <span className="popup-menu-item-icon" style={{ marginRight: 8 }}>
                  {typeof item.icon === 'string' ? (
                    item.icon.startsWith('bi ') ? (
                      <i className={item.icon}></i>
                    ) : (
                      <span>{item.icon}</span>
                    )
                  ) : (
                    item.icon
                  )}
                </span>
              )}
              <span className="popup-menu-item-label" style={{ flex: 1, minWidth: 0 }}>
                {item.labelNode ?? item.label}
              </span>
            </button>

            {item.rightAction && !item.disabled && (
              <button
                type="button"
                className="popup-menu-item-right-action"
                title={item.rightAction.title || ''}
                aria-label={item.rightAction.ariaLabel || item.rightAction.title || 'Menu action'}
                onClick={() => {
                  item.rightAction.onClick?.();
                  if (item.rightAction.closeOnClick !== false) {
                    onClose();
                  }
                }}
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 8px',
                  background: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  borderRadius: '4px',
                  color: '#64748b',
                  lineHeight: 1,
                  cursor: 'pointer'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {typeof item.rightAction.icon === 'string' ? (
                  <i className={item.rightAction.icon}></i>
                ) : (
                  item.rightAction.icon
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </CSSTransition>,
    document.body
  );
}
